import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import "../../GenericTable.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SearchQueryEvent } from "../../Models/searchQueryEvent";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";

export interface ListSearchBarProps {
  onSearchQueryChange: (nextSearchQuery: SearchQueryEvent) => void | undefined;
  searchQuery?: string;
  border?: boolean;
  grayBG?: boolean;
}

function TableSearchBar({
  onSearchQueryChange,
  searchQuery,
  border = false,
  grayBG = false,
}: ListSearchBarProps) {
  const { t } = useTranslation();
  // Controlled input, initialised from the active query so the field still
  // shows the current search term after navigating back to the list (the
  // term round-trips through the URL via usePageQuery -> ListHeader).
  const [value, setValue] = useState<string>(searchQuery ?? "");

  // Last search term actually emitted (always trimmed). Serves two purposes:
  // - skip emits whose trimmed value is unchanged (no redundant refetches,
  //   e.g. trailing spaces), while an intentional clear ("") still fires;
  // - tell *external* query changes (back/forward navigation) apart from the
  //   echo of our own emit coming back down as a prop, so typing is never
  //   clobbered mid-word.
  const lastEmittedRef = useRef<string>((searchQuery ?? "").trim());

  // Create a debounced version of the search callback
  const debouncedSearch = useMemo(
    () =>
      debounce((rawTerm: string) => {
        const searchTerm = rawTerm.trim();
        if (searchTerm === lastEmittedRef.current) {
          return;
        }
        lastEmittedRef.current = searchTerm;
        onSearchQueryChange({ searchTerm });
      }, 350),
    [onSearchQueryChange]
  );

  // Sync from an external query change (e.g. browser back/forward). Our own
  // emits come back as a prop equal to lastEmittedRef and are ignored.
  useEffect(() => {
    const external = searchQuery ?? "";
    if (external.trim() !== lastEmittedRef.current) {
      lastEmittedRef.current = external.trim();
      setValue(external);
      debouncedSearch.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    debouncedSearch(newValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Search immediately instead of waiting out the debounce delay.
      debouncedSearch.flush();
    }
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div
      className={`w-full text-xs rounded-md h-9 flex space-x-2 items-center pl-2 hover:cursor-text ${
        border ? "border border-borderGray" : ""
      } ${grayBG ? "bg-gray-100 hover:bg-white" : "bg-white"}`}
    >
      <span>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </span>
      <input
        placeholder={t("search")}
        className="w-full h-full border-none text-gray-600 text-xs font-medium outline-none pr-4 bg-transparent"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

export default TableSearchBar;
